import { 
  CheckCircle2, 
  FileText,
  MessageSquare,
  Handshake,
  ClipboardCheck,
  HeartHandshake
} from 'lucide-react';
import { Button } from '../components/ui/button';

const processSteps = [
  {
    step: '1',
    title: 'Resume Review',
    description: 'We carefully review your experience and goals to assess alignment with current opportunities.',
  },
  {
    step: '2',
    title: 'Conversation & Positioning',
    description: 'If there is alignment, we schedule a conversation to better understand your background and refine how your experience is presented.',
  },
  {
    step: '3',
    title: 'Strategic Introductions',
    description: 'We introduce you to employers where your skills and experience match real organizational needs.',
  },
  {
    step: '4',
    title: 'Preparation & Support',
    description: 'We help you prepare for interviews and ensure expectations are clear on both sides.',
  },
  {
    step: '5',
    title: 'Continued Engagement',
    description: 'We remain involved through the hiring process and beyond placement.',
  },
];

const whoWeWorkWith = [
  'Bring 15-20+ years of experience',
  'Have demonstrated leadership, operational, or technical expertise',
  'Are seeking stability and long-term contribution',
  'Value accountability and meaningful work',
];

const advocacyPoints = [
  'Honest feedback about market fit',
  'Resume positioning grounded in today\'s hiring landscape',
  'Direct employer connections when alignment exists',
  'Clear communication throughout the process',
];

export default function Candidates() {

  return (
    <div data-testid="candidates-page">
      {/* Hero Section */}
      <section className="relative bg-slate-800 overflow-hidden pt-40 md:pt-[calc(12rem+10vh)] pb-12 md:pb-16" data-testid="candidates-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700" />
        
        <div className="relative container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-3xl">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-5 animate-fade-in-up"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              You've Built a Career.<br />
              <span className="text-purple-200 italic">Let's Move It Forward.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mb-8 leading-relaxed animate-fade-in-up animation-delay-100">
              Aim Hire supports experienced professionals navigating career transitions—with clarity, 
              preparation, and direct access to employers who value experience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
              <a href="#submit-resume">
                <Button className="bg-white hover:bg-slate-100 text-slate-900 rounded-full px-10 py-7 text-xl font-medium">
                  Submit Your Resume
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Professional Support Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="professional-support">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="max-w-2xl mx-auto text-center">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Professional Support, Thoughtfully Delivered
            </h2>
            <p className="text-base text-slate-600 mb-6">
              A career transition can be complex—even for accomplished professionals. Our role is to 
              help you position your experience clearly, navigate the process strategically, and 
              connect with employers where you can contribute meaningfully.
            </p>
            <div className="flex flex-col md:flex-row justify-center gap-3 md:gap-6 text-slate-700 text-sm font-medium">
              <span>We are not a job board.</span>
              <span>We are not a high-volume recruiter.</span>
            </div>
            <p className="text-slate-900 font-semibold mt-3 text-base">
              We work intentionally—and we stay involved.
            </p>
          </div>
        </div>
      </section>

      {/* Our Process Section */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="our-process">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">
              What to Expect
            </p>
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Our Process
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="flex gap-4 mb-6 last:mb-0"
                data-testid={`process-step-${index}`}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
                  {step.step}
                </div>
                <div className="pt-1">
                  <h3 
                    className="text-lg font-semibold text-slate-900 mb-1"
                    style={{ fontFamily: 'var(--font-heading)' }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="py-16 md:py-20 bg-white" data-testid="who-we-work-with">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Left - Who We Work With */}
            <div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Who We Typically Work With
              </h2>
              <p className="text-base text-slate-600 mb-4">
                We most often support professionals who:
              </p>
              <ul className="space-y-3 mb-6">
                {whoWeWorkWith.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-600 text-sm italic border-l-4 border-purple-200 pl-4">
                Experience is not something to minimize. It is something to position well.
              </p>
            </div>

            {/* Right - Advocacy */}
            <div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Advocacy With Professional Standards
              </h2>
              <p className="text-base text-slate-600 mb-4">
                We believe experienced professionals deserve to be presented seriously. What that means for you:
              </p>
              <ul className="space-y-3 mb-6">
                {advocacyPoints.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-slate-700 text-sm font-medium">
                Our goal is not simply to place resumes.{' '}
                <span className="text-slate-900">It is to facilitate strong, lasting professional matches.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Submit Resume Section */}
      <section id="submit-resume" className="py-16 md:py-20 bg-slate-800 scroll-mt-32" data-testid="submit-resume">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 
                className="text-2xl md:text-3xl font-bold text-white mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Ready to Begin?
              </h2>
              <p className="text-base text-slate-300 mb-4">
                Submit your resume and share a brief note about the type of role you're seeking. 
                We're here to help you move forward with clarity and confidence.
              </p>
              <p className="text-slate-400 text-sm">
                We review every submission carefully. Due to volume, we may not be able to respond 
                immediately—but every resume is considered.
              </p>
            </div>

            {/* Application Form - RecruiterPM Link */}
            <div className="bg-slate-50 rounded-xl p-8 md:p-12 text-center" data-testid="candidate-form">
              <h3 
                className="text-xl font-semibold text-slate-900 mb-4"
                style={{ fontFamily: 'var(--font-heading)' }}
              >
                Apply Now
              </h3>
              <p className="text-slate-600 mb-6">
                Click below to submit your resume and application through our secure portal.
              </p>
              <a 
                href="https://portal.recruiterpm.com/careers/show/aim-hire/0/apply"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button 
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-12 py-6 text-xl font-medium"
                  data-testid="candidate-apply-btn"
                >
                  Submit Your Application
                </Button>
              </a>
              <p className="text-sm text-slate-500 mt-4">
                You will be redirected to our secure application portal.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
